import dayjs from '~/timeInit';

import type { Dayjs } from '~/services/TimeService';

import type { RollingDuration, VendorSummaryGranularity } from '~/types/services/vendor';

export const MONTH_FORMAT = 'YYYY-MM';
export const QUARTER_REGEX = /^(\d{4})-Q([1-4])$/;
export const ROLLING_PERIOD_REGEX = /^(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})$/;

export const ROLLING_DURATION_MONTHS: Record<RollingDuration, number> = {
  half_year: 6,
};

export function quarterFromMonthIndex(monthIndex: number): 1 | 2 | 3 | 4 {
  return (Math.floor(monthIndex / 3) + 1) as 1 | 2 | 3 | 4;
}

export function parsePeriod(
  period: string,
  granularity: VendorSummaryGranularity,
): { start: Dayjs; end: Dayjs } | null {
  if (granularity === 'rolling') {
    const match = ROLLING_PERIOD_REGEX.exec(period);
    if (!match) {
      return null;
    }
    const start = dayjs(match[1], 'YYYY-MM-DD', true);
    const end = dayjs(match[2], 'YYYY-MM-DD', true);
    return start.isValid() && end.isValid() && end.isAfter(start) ? { start, end } : null;
  }
  if (granularity === 'month') {
    const m = dayjs(period, MONTH_FORMAT, true);
    return m.isValid() ? { start: m.startOf('month'), end: m.endOf('month') } : null;
  }
  const match = QUARTER_REGEX.exec(period);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const quarter = Number(match[2]);
  const startMonth = (quarter - 1) * 3;
  const start = dayjs().year(year).month(startMonth).startOf('month');
  const end = start.add(2, 'month').endOf('month');
  return start.isValid() ? { start, end } : null;
}

export function formatPeriodForUrl(date: Dayjs, granularity: 'month' | 'quarter'): string {
  if (granularity === 'month') {
    return date.format(MONTH_FORMAT);
  }
  return `${date.year()}-Q${quarterFromMonthIndex(date.month())}`;
}

export function defaultPeriod(granularity: VendorSummaryGranularity, duration: RollingDuration = 'half_year'): string {
  // Monthly defaults to the last *closed* month (no partial data); quarterly intentionally
  // defaults to the current quarter for live visibility vs. trailing-4Q/LY, matching Tableau KTR.
  if (granularity === 'rolling') {
    const end = dayjs();
    const start = end.subtract(ROLLING_DURATION_MONTHS[duration], 'months');
    return `${start.format('YYYY-MM-DD')}_${end.format('YYYY-MM-DD')}`;
  }
  if (granularity === 'month') {
    return dayjs().subtract(1, 'month').startOf('month').format(MONTH_FORMAT);
  }
  const lastMonth = dayjs().subtract(1, 'month');
  return `${lastMonth.year()}-Q${quarterFromMonthIndex(lastMonth.month())}`;
}

export function periodLabel(period: string, granularity: VendorSummaryGranularity): string {
  const parsed = parsePeriod(period, granularity);
  if (!parsed) {
    return period;
  }
  if (granularity === 'rolling') {
    const sameYear = parsed.start.year() === parsed.end.year();
    const startFormat = sameYear ? 'D MMM' : 'D MMM YYYY';
    return `${parsed.start.format(startFormat)} – ${parsed.end.format('D MMM YYYY')}`;
  }
  if (granularity === 'month') {
    return parsed.start.format('MMMM YYYY');
  }
  const match = QUARTER_REGEX.exec(period);
  return match ? `Q${match[2]} ${match[1]}` : period;
}

// Short form (no year) for inline use in the email body, e.g. "* Q2: $744,600". Only ever
// called on a period already confirmed quarter-shaped, so the match can't fail.
export function quarterShort(period: string): string {
  const match = QUARTER_REGEX.exec(period);
  return `Q${match[2]}`;
}

// Short month-range form (mirrors quarterShort), e.g. "Mar–Sep".
export function rollingShort(period: string): string {
  const match = ROLLING_PERIOD_REGEX.exec(period);
  return `${dayjs(match[1]).format('MMM')}–${dayjs(match[2]).format('MMM')}`;
}

export function granularityForPeriod(period: string): VendorSummaryGranularity {
  if (ROLLING_PERIOD_REGEX.test(period)) {
    return 'rolling';
  }
  return QUARTER_REGEX.test(period) ? 'quarter' : 'month';
}

export function trailingAvgLabel(granularity: VendorSummaryGranularity, periodsInAvg: number | null): string {
  const periods = periodsInAvg ?? (granularity === 'month' ? 12 : 4);
  const unit = granularity === 'month' ? 'month' : 'quarter';
  return `${periods} ${unit} avg`;
}

export function currentPeriodLabel(granularity: VendorSummaryGranularity): string {
  return granularity === 'month' ? 'last month' : 'last quarter';
}

// Generate the picker options: a window of recent closed periods, most-recent first.
export function recentQuarterOptions(count = 12): Array<string> {
  const out: Array<string> = [];
  const cursor = dayjs().subtract(1, 'month');
  let q = quarterFromMonthIndex(cursor.month());
  let y = cursor.year();
  for (let i = 0; i < count; i += 1) {
    out.push(`${y}-Q${q}`);
    q -= 1;
    if (q < 1) {
      q = 4;
      y -= 1;
    }
  }
  return out;
}
